import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Row, Col, Input, Select, Pagination, Spin, Empty, Typography, Tag, Button, Space, Checkbox, Divider, Skeleton, Tooltip,
} from 'antd';
import {
  SearchOutlined, FilterOutlined, CloseOutlined, SortAscendingOutlined, AppstoreOutlined,
  SafetyCertificateOutlined, GlobalOutlined, ReloadOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import ToolCard from '../components/ToolCard';
import CategoryIcon from '../components/CategoryIcon';
import { toolsAPI, categoriesAPI, userAPI } from '../services/api';
import { Tool, Category, PaginationInfo, SearchHistoryItem } from '../types';
import { usePageSeo } from '../utils/usePageSeo';
import { AdSlot } from '../components/AdsBootstrap';
import { useLanguage } from '../store/LanguageContext';
import { useAuth } from '../store/AuthContext';
import { getCategoryLabel } from '../utils/categoryLocale';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const JOB_CHIPS_ZH = [
  { label: '写代码', path: '/news/code-choice' },
  { label: '出图', path: '/news/image-choice' },
  { label: '做视频', path: '/news/video-choice' },
  { label: '中文聊天', path: '/news/cn-chat-choice' },
  { label: '看推理', path: '/news/deepseek' },
];
const JOB_CHIPS_EN = [
  { label: 'Coding', path: '/news/code-choice' },
  { label: 'Images', path: '/news/image-choice' },
  { label: 'Video', path: '/news/video-choice' },
  { label: 'CN chat', path: '/news/cn-chat-choice' },
  { label: 'Reasoning', path: '/news/deepseek' },
];

const ToolsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const zh = language === 'zh-CN';

  usePageSeo({
    title: language === 'zh-CN' ? 'AI工具大全' : 'AI Tools Directory',
    description: language === 'zh-CN'
      ? '浏览和对比中文 AI 工具目录，按分类、热度与用途筛选写作、图像、编程、对话等应用。'
      : 'Browse and compare AI tools by category, popularity, pricing, and use case.',
    path: '/tools',
  });

  const [tools, setTools] = useState<Tool[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, pageSize: 12, total: 0, totalPages: 0 });
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [categoryId, setCategoryId] = useState<string>(searchParams.get('category') || '');
  const [pricingType, setPricingType] = useState<string>(searchParams.get('pricing') || '');
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sort') || 'hot_score');
  const [page, setPage] = useState<number>(Number(searchParams.get('page')) || 1);
  const [availabilityCN, setAvailabilityCN] = useState<string>(searchParams.get('availability_cn') || '');
  const [complianceTier, setComplianceTier] = useState<string>(searchParams.get('compliance_tier') || '');
  const [supportsInvoice, setSupportsInvoice] = useState<boolean>(searchParams.get('supports_invoice') === 'true');
  const [supportsContract, setSupportsContract] = useState<boolean>(searchParams.get('supports_contract') === 'true');
  const [supportsOnPrem, setSupportsOnPrem] = useState<boolean>(searchParams.get('supports_on_prem') === 'true');
  // Default: curated (featured). Full dump catalog only when view=all or user filters.
  const [browseAll, setBrowseAll] = useState<boolean>(searchParams.get('view') === 'all');

  const pricingOptions = useMemo(() => [
    { label: t('tools.pricingAll'), value: '' },
    { label: t('pricing.free'), value: 'free' },
    { label: t('pricing.freemium'), value: 'freemium' },
    { label: t('pricing.paid'), value: 'paid' },
    { label: t('pricing.contact'), value: 'contact' },
  ], [language]);

  const availabilityCNOptions = useMemo(() => [
    { label: t('tools.pricingAll'), value: '' as const },
    { label: `🇨🇳 ${t('availability.cn_native')}`, value: 'cn_native' as const },
    { label: `✅ ${t('availability.direct')}`, value: 'direct' as const },
    { label: `🔗 ${t('availability.mirror')}`, value: 'mirror' as const },
    { label: `🔒 ${t('availability.vpn')}`, value: 'vpn' as const },
    { label: `🚫 ${t('availability.blocked')}`, value: 'blocked' as const },
  ], [language]);

  const sortOptions = useMemo(() => [
    { label: t('tools.sortHotScore'), value: 'hot_score' },
    { label: t('tools.sortFunding'), value: 'funding_amount' },
    { label: t('tools.sortViewCount'), value: 'view_count' },
    { label: t('tools.sortRating'), value: 'rating' },
    { label: t('tools.sortNewest'), value: 'created_at' },
    { label: t('tools.sortReviewCount'), value: 'review_count' },
    { label: t('tools.sortName'), value: 'name' },
    { label: t('tools.sortCompliance'), value: 'compliance_score' },
  ], [language]);

  const complianceTierOptions = useMemo(() => [
    { label: t('tools.pricingAll'), value: '' },
    { label: t('tier.T1_full'), value: 'T1_full' },
    { label: t('tier.T2_partial'), value: 'T2_partial' },
    { label: t('tier.T3_none'), value: 'T3_none' },
    { label: t('tier.pending'), value: 'pending' },
  ], [language]);

  useEffect(() => {
    categoriesAPI.getAll().then((res) => {
      const cats = res.data.categories || [];
      setCategories(cats);
      // sitemap 使用 category=slug；列表 UI 用 id，加载后归一化
      const raw = searchParams.get('category') || '';
      if (raw && !/^\d+$/.test(raw)) {
        const match = cats.find((c: Category) => c.slug === raw);
        if (match) setCategoryId(String(match.id));
      }
    });
  }, []);

  useEffect(() => {
    const fetchSearchHistory = async () => {
      if (!user) {
        setSearchHistory([]);
        return;
      }
      try {
        const res = await userAPI.getSearchHistory({ limit: 8 });
        setSearchHistory(res.data.history || []);
      } catch {
        setSearchHistory([]);
      }
    };
    fetchSearchHistory();
  }, [user]);

  const hasActiveFilters = !!(
    search || categoryId || pricingType || availabilityCN || complianceTier ||
    supportsInvoice || supportsContract || supportsOnPrem
  );
  const useCurated = !browseAll && !hasActiveFilters;

  const fetchTools = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        page,
        limit: useCurated ? 24 : 12,
        sort: useCurated ? 'hot_score' : sortBy,
      };
      if (useCurated) {
        params.featured = true;
      }
      if (search) params.search = search;
      if (categoryId) params.category = categoryId;
      if (pricingType) params.pricing_type = pricingType;
      if (availabilityCN) params.availability_cn = availabilityCN;
      if (complianceTier) params.compliance_tier = complianceTier;
      if (supportsInvoice) params.supports_invoice = true;
      if (supportsContract) params.supports_contract = true;
      if (supportsOnPrem) params.supports_on_prem = true;

      const res = await toolsAPI.getTools(params);
      setTools(res.data.tools || []);
      const backendPagination = res.data.pagination || { page: 1, limit: 12, total: 0, totalPages: 0 };
      setPagination({
        page: backendPagination.page,
        pageSize: backendPagination.limit ?? (useCurated ? 24 : 12),
        total: backendPagination.total,
        totalPages: backendPagination.totalPages,
      });

      if (user && search.trim()) {
        await userAPI.createSearchHistory({
          keyword: search.trim(),
          result_count: backendPagination.total || 0,
        });
      }
    } catch (error) {
      console.error('Failed to fetch tools:', error);
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, search, categoryId, pricingType, availabilityCN, complianceTier, supportsInvoice, supportsContract, supportsOnPrem, useCurated, user]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  // 同步 URL 参数
  useEffect(() => {
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (categoryId) params.category = categoryId;
    if (pricingType) params.pricing = pricingType;
    if (sortBy !== 'hot_score') params.sort = sortBy;
    if (page > 1) params.page = String(page);
    if (availabilityCN) params.availability_cn = availabilityCN;
    if (complianceTier) params.compliance_tier = complianceTier;
    if (supportsInvoice) params.supports_invoice = 'true';
    if (supportsContract) params.supports_contract = 'true';
    if (supportsOnPrem) params.supports_on_prem = 'true';
    if (browseAll) params.view = 'all';
    setSearchParams(params, { replace: true });
  }, [search, categoryId, pricingType, sortBy, page, availabilityCN, complianceTier, supportsInvoice, supportsContract, supportsOnPrem, browseAll, setSearchParams]);

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const [categoryFilter, setCategoryFilter] = useState('');

  const getCatName = (cat: Category) => getCategoryLabel(cat, language);

  // 按工具数量排序, 并支持搜索过滤（同时匹配中英文名）
  const filteredCategories = useMemo(() => {
    const sorted = [...categories]
      .filter((c) => (Number(c.tool_count) || 0) > 0)
      .sort((a, b) => (Number(b.tool_count) || 0) - (Number(a.tool_count) || 0));
    if (!categoryFilter.trim()) return sorted;
    const kw = categoryFilter.trim().toLowerCase();
    return sorted.filter(c =>
      c.name.toLowerCase().includes(kw) ||
      (c.name_zh && c.name_zh.toLowerCase().includes(kw))
    );
  }, [categories, categoryFilter, language]);

  const handleCategoryChange = (id: string) => {
    setCategoryId(id === categoryId ? '' : id);
    setPage(1);
  };

  const handlePricingChange = (value: string) => {
    setPricingType(value);
    setPage(1);
  };

  const clearFilters = () => {
    setSearch('');
    setCategoryId('');
    setPricingType('');
    setSortBy('hot_score');
    setPage(1);
    setAvailabilityCN('');
    setComplianceTier('');
    setSupportsInvoice(false);
    setSupportsContract(false);
    setSupportsOnPrem(false);
  };

  const hasFilters = search || categoryId || pricingType || sortBy !== 'hot_score' || availabilityCN || complianceTier || supportsInvoice || supportsContract || supportsOnPrem;

  // 分类筛选 - 显示前18个，按 tool_count 排序
  const topCategories = useMemo(
    () => filteredCategories.slice(0, 18),
    [filteredCategories]
  );

  // 活跃筛选标签
  const activeFilters: { label: string; onClose: () => void }[] = [];
  if (categoryId) {
    const cat = categories.find(c => String(c.id) === categoryId);
    if (cat) activeFilters.push({ label: getCatName(cat), onClose: () => { setCategoryId(''); setPage(1); } });
  }
  if (pricingType) {
    const p = pricingOptions.find(o => o.value === pricingType);
    if (p) activeFilters.push({ label: p.label, onClose: () => { setPricingType(''); setPage(1); } });
  }
  if (availabilityCN) {
    const a = availabilityCNOptions.find(o => o.value === availabilityCN);
    if (a) activeFilters.push({ label: a.label, onClose: () => { setAvailabilityCN(''); setPage(1); } });
  }
  if (complianceTier) {
    const c = complianceTierOptions.find(o => o.value === complianceTier);
    if (c) activeFilters.push({ label: c.label, onClose: () => { setComplianceTier(''); setPage(1); } });
  }
  if (supportsInvoice) activeFilters.push({ label: t('tools.supportsInvoice'), onClose: () => { setSupportsInvoice(false); setPage(1); } });
  if (supportsContract) activeFilters.push({ label: t('tools.supportsContract'), onClose: () => { setSupportsContract(false); setPage(1); } });
  if (supportsOnPrem) activeFilters.push({ label: t('tools.supportsOnPrem'), onClose: () => { setSupportsOnPrem(false); setPage(1); } });

  return (
    <div style={{ background: '#f3f4f6', minHeight: '100vh' }}>
      {/* ===== 顶部搜索栏 ===== */}
      <div style={{ background: '#fff', borderBottom: '1px solid #f0f0f0', padding: '20px 0' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Search
              placeholder={t('tools.searchFullPlaceholder')}
              allowClear
              enterButton={<><SearchOutlined /> {t('common.search')}</>}
              size="large"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onSearch={handleSearch}
              style={{ flex: 1, minWidth: 240 }}
            />
            <Select
              value={sortBy}
              onChange={(val) => { setSortBy(val); setPage(1); }}
              size="large"
              style={{ width: 148 }}
              suffixIcon={<SortAscendingOutlined />}
            >
              {sortOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
              ))}
            </Select>
          </div>

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>{zh ? '按任务先看' : 'Start from a job'}</Text>
            {(zh ? JOB_CHIPS_ZH : JOB_CHIPS_EN).map((job) => (
              <Tag
                key={job.path}
                style={{ cursor: 'pointer', borderRadius: 20, margin: 0 }}
                onClick={() => navigate(job.path)}
              >
                {job.label}
              </Tag>
            ))}
            <span style={{ flex: 1 }} />
            <Button
              type={useCurated ? 'default' : 'primary'}
              size="small"
              onClick={() => { setBrowseAll((v) => !v); setPage(1); }}
            >
              {useCurated
                ? (zh ? '浏览全部目录' : 'Browse full catalog')
                : (zh ? '回到精选' : 'Back to featured')}
            </Button>
          </div>

          {/* 搜索历史 */}
          {user && searchHistory.length > 0 && (
            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text type="secondary" style={{ fontSize: 12 }}>{t('tools.recentSearch')}: </Text>
              {searchHistory.map((item) => (
                <Tag
                  key={item.id}
                  style={{ cursor: 'pointer', borderRadius: 20, fontSize: 12 }}
                  onClick={() => handleSearch(item.keyword)}
                >
                  {item.keyword}
                </Tag>
              ))}
              <Button type="link" size="small" style={{ padding: '0 4px', fontSize: 12 }}
                onClick={async () => { await userAPI.clearSearchHistory(); setSearchHistory([]); }}>
                {t('tools.clearHistory')}
              </Button>
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 24px 48px' }}>
        <Row gutter={24} align="top">
          {/* ===== 左侧筛选栏 ===== */}
          <Col xs={24} md={6} lg={5}>
            <div style={{
              background: '#fff',
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid #f0f0f0',
              position: 'sticky',
              top: 80,
            }}>
              {/* 筛选标题 */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 15, color: '#111827' }}>
                  <FilterOutlined style={{ marginRight: 6, color: '#16a34a' }} />{t('tools.filter')}
                </span>
                {hasFilters && (
                  <Button type="link" size="small" onClick={clearFilters} style={{ padding: 0, fontSize: 12, color: '#16a34a' }}>
                    <ReloadOutlined /> {t('tools.resetFilter')}
                  </Button>
                )}
              </div>

              {/* 分类 */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('tools.category')}
                </div>
                <Input
                  placeholder={t('tools.searchCategory')}
                  size="small"
                  allowClear
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                  style={{ marginBottom: 10, borderRadius: 8 }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 320, overflowY: 'auto' }}>
                  {filteredCategories.map((cat) => {
                    const isActive = String(cat.id) === categoryId;
                    return (
                      <div
                        key={cat.id}
                        onClick={() => handleCategoryChange(String(cat.id))}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '6px 10px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: isActive ? '#f0fdf4' : 'transparent',
                          color: isActive ? '#16a34a' : '#374151',
                          fontWeight: isActive ? 600 : 400,
                          fontSize: 13.5,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          <CategoryIcon name={cat.icon} style={{ marginRight: 6 }} /> {getCatName(cat)}
                        </span>
                        <span style={{ fontSize: 11, color: isActive ? '#16a34a' : '#9ca3af', marginLeft: 4, flexShrink: 0 }}>
                          {Number(cat.tool_count) >= 500 ? '·' : (cat.tool_count || 0)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 定价 */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('tools.pricing')}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {pricingOptions.map((opt) => {
                    const isActive = pricingType === opt.value;
                    return (
                      <Tag
                        key={opt.value}
                        onClick={() => { handlePricingChange(opt.value); }}
                        style={{
                          cursor: 'pointer',
                          borderRadius: 20,
                          padding: '3px 12px',
                          margin: 0,
                          background: isActive ? '#16a34a' : '#f3f4f6',
                          color: isActive ? '#fff' : '#4b5563',
                          border: 'none',
                          fontWeight: isActive ? 600 : 400,
                          transition: 'all 0.15s',
                        }}
                      >
                        {opt.label}
                      </Tag>
                    );
                  })}
                </div>
              </div>

              {/* 中国可用性 */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t('tools.availabilityCN')}
                  <Tooltip title={t('disclaimer.section2Content')}>
                    <InfoCircleOutlined style={{ color: '#999', cursor: 'pointer', fontSize: 13 }} />
                  </Tooltip>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {availabilityCNOptions.map((opt) => {
                    const isActive = availabilityCN === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => { setAvailabilityCN(opt.value); setPage(1); }}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: isActive ? '#f0fdf4' : 'transparent',
                          color: isActive ? '#16a34a' : '#374151',
                          fontWeight: isActive ? 600 : 400,
                          fontSize: 13,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {opt.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 合规等级 */}
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('tools.complianceTier')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {complianceTierOptions.map((opt) => {
                    const isActive = complianceTier === opt.value;
                    return (
                      <div
                        key={opt.value}
                        onClick={() => { setComplianceTier(opt.value); setPage(1); }}
                        style={{
                          padding: '5px 10px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          background: isActive ? '#f0fdf4' : 'transparent',
                          color: isActive ? '#16a34a' : '#374151',
                          fontWeight: isActive ? 600 : 400,
                          fontSize: 13,
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f5f5f5'; }}
                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {opt.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 企业采购 */}
              <div style={{ padding: '16px 20px' }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#555', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('tools.enterprise')}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Checkbox checked={supportsInvoice} onChange={e => { setSupportsInvoice(e.target.checked); setPage(1); }}>
                    <span style={{ fontSize: 13 }}>{t('tools.supportsInvoice')}</span>
                  </Checkbox>
                  <Checkbox checked={supportsContract} onChange={e => { setSupportsContract(e.target.checked); setPage(1); }}>
                    <span style={{ fontSize: 13 }}>{t('tools.supportsContract')}</span>
                  </Checkbox>
                  <Checkbox checked={supportsOnPrem} onChange={e => { setSupportsOnPrem(e.target.checked); setPage(1); }}>
                    <span style={{ fontSize: 13 }}>{t('tools.supportsOnPrem')}</span>
                  </Checkbox>
                </div>
              </div>
            </div>
          </Col>

          {/* ===== 右侧工具列表 ===== */}
          <Col xs={24} md={18} lg={19}>
            {/* 统计行 + 活跃筛选标签 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <Text style={{ fontSize: 14, color: '#6b7280' }}>
                  <AppstoreOutlined style={{ marginRight: 4 }} />
                  {useCurated
                    ? (zh ? `精选 ${pagination.total} 款 · 先按任务选型` : `Featured ${pagination.total} · start from a job`)
                    : pagination.total >= 10000 && !hasActiveFilters
                      ? t('tools.narrowHint')
                      : t('tools.totalFound', { count: String(pagination.total) })}
                  {search && <span style={{ color: '#16a34a', marginLeft: 4 }}>「{search}」</span>}
                </Text>
                {activeFilters.map((f, i) => (
                  <Tag
                    key={i}
                    closable
                    onClose={f.onClose}
                    style={{ borderRadius: 20, margin: 0, fontSize: 12 }}
                    color="success"
                  >
                    {f.label}
                  </Tag>
                ))}
                {activeFilters.length > 0 && (
                  <Button type="link" size="small" onClick={clearFilters} style={{ padding: '0 4px', fontSize: 12 }}>
                    {t('tools.clearAll')}
                  </Button>
                )}
              </div>
            </div>

            {/* 工具列表 */}
            {loading ? (
              <Row gutter={[16, 16]}>
                {Array(12).fill(0).map((_, i) => (
                  <Col key={i} xs={24} sm={12} lg={8} xl={6}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 20 }}>
                      <Skeleton active avatar paragraph={{ rows: 3 }} />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : tools.length > 0 ? (
              <>
                <Row gutter={[16, 16]}>
                  {tools.map((tool) => (
                    <Col key={tool.id} xs={24} sm={12} lg={8} xl={6}>
                      <ToolCard tool={tool} />
                    </Col>
                  ))}
                </Row>

                {pagination.totalPages > 1 && (
                  <div style={{ textAlign: 'center', marginTop: 40, paddingBottom: 16 }}>
                    <Pagination
                      current={pagination.page}
                      total={pagination.total}
                      pageSize={pagination.pageSize}
                      onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      showSizeChanger={false}
                      showLessItems
                      showQuickJumper={pagination.total < 5000}
                      showTotal={(total) =>
                        total >= 10000 && !search && !categoryId
                          ? t('tools.narrowHint')
                          : t('tools.totalFound', { count: String(total) })
                      }
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, padding: '60px 24px' }}>
                <Empty
                  description={
                    <span style={{ color: '#999', fontSize: 15 }}>
                      {search ? t('tools.noResultSearch', { keyword: search }) : t('tools.noResultGeneric')}
                    </span>
                  }
                >
                  <Button type="primary" onClick={clearFilters} style={{ borderRadius: 20, padding: '0 24px' }}>
                    {t('tools.clearFilter')}
                  </Button>
                </Empty>
              </div>
            )}
          </Col>
        </Row>
      </div>
      <AdSlot placement="list" />
    </div>
  );
};

export default ToolsPage;
